import { useTranslation } from "@sagot/i18n";
import { useFormatters, type BillingOverview, type BillingPlan } from "../shared";
import { storageText } from "./billing-model";

/** Human sentences about plan limits (upgrade gains, downgrade impact). */
export function usePlanLimits() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const list = new Intl.ListFormat(fmt.locale, { style: "long", type: "conjunction" });
  const limitText = (plan: BillingPlan) => ({
    messages: plan.message_credits === null ? t("billing.usage.unlimited") : fmt.number(plan.message_credits),
    agents: plan.agents === null ? t("billing.usage.unlimited") : fmt.number(plan.agents),
    storage: plan.storage_mb === null ? t("billing.usage.unlimited") : storageText(plan.storage_mb),
    seats: plan.seats === null ? t("billing.usage.unlimited") : fmt.number(plan.seats),
  });
  const summary = (plan: BillingPlan) => {
    const l = limitText(plan);
    return list.format([
      t("billing.limits.messages", { value: l.messages }),
      t("billing.limits.agents", { value: l.agents }),
      t("billing.limits.storage", { value: l.storage }),
      t("billing.limits.seats", { value: l.seats }),
    ]);
  };
  const downgradeImpact = (plan: BillingPlan, overview: BillingOverview) => {
    const l = limitText(plan);
    const over =
      (plan.agents !== null && overview.agents.used > plan.agents) ||
      (plan.seats !== null && overview.seats.used > plan.seats) ||
      (plan.storage_mb !== null && overview.storage_mb.used > plan.storage_mb);
    const base = t("billing.change.downgradeBody", {
      plan: plan.name,
      agents: l.agents,
      seats: l.seats,
      storage: l.storage,
      usedAgents: fmt.number(overview.agents.used),
      usedSeats: fmt.number(overview.seats.used),
      usedStorage: storageText(overview.storage_mb.used),
    });
    return over ? `${base} ${t("billing.change.downgradeOver")}` : base;
  };
  return { summary, downgradeImpact };
}
